import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ADKDetailsViewModel } from '../../../models/adk-details-view.model';
import { CurrentUserModel, CustomerPaymentsOutput } from '../../../models/account-ledger.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { CheckOutService } from '../../../services/checkout.service';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageService } from '../../../services/manage.service';
import { FormBuilder } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { SimpleMsgPopupComponent } from '../../../popup-collection/simple-msg/simple-msg-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { SubscriptionApiResponse } from '../../../models/api-response.model';
import { MicrosoftCloudAgreementComponent } from '../../../popup-collection/microsoft-cloud-agreement/microsoft-cloud-agreement.component';
import { AuthService } from '../../../services/auth.service';
import { ApiResponse } from '../../../models/api-response.model';
import { CommonService } from '../../../services/common.service';

@Component({
  selector: 'dd-csp-end-user-details',
  templateUrl: './csp-end-user-detail.component.html',
  styleUrls: ['./csp-end-user-detail.component.css']
})
export class CSPEndUserDetailsComponent implements OnInit, OnDestroy {

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private _details = new BehaviorSubject<ADKDetailsViewModel>(null);
  private tenantId: string;
  public currentUserModel: CurrentUserModel;

  @Input() isSubscriptionResponseLoading: boolean;
  @Input() subscriptionResponse: SubscriptionApiResponse;
  @Input() set details(value: ADKDetailsViewModel) { 
    this._details.next(value); 
  };

  get details() {
    return this._details.getValue();
  }
  loadContent: boolean = false;
  public paymentMethod: CustomerPaymentsOutput;
  public prefix = '';
  public form = this.formBuilder.group({
    azureRIPurchaseToggle: [{ value: false, disabled: true }],
    azureCMViewToggle: [{ value: false, disabled: true }],
    azureSavingsPlanToggle: [{ value: false, disabled: true }],
  });

  public ARIPRTLoadingSpinner = true;
  public ACMVTLoadingSpinner = true;
  public showAzureCMToggle = true;
  public AZSPLoadingSpinner = true;
  public showAzureSPToggle = true;

  constructor(
    private manageService: ManageService,
    private checkOutService: CheckOutService,
    private commonService: CommonService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    public authService: AuthService
  ) { }

  async ngOnInit(): Promise<void> {
    const currentUser: CurrentUserModel = await this.authService.getLoginInfo().pipe(takeUntil(this.ngUnsubscribe)).toPromise();
    this.currentUserModel = currentUser;

    this._details.subscribe(d => {
      if (d) {
        this.paymentMethod = null;
        if (this.details?.customer?.out_digital_wallet_id) {
          this.checkOutService
            .GetSubscriptionPaymentMethodList()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((res) => {
              if (res) {
                this.paymentMethod = res.find((m) => m.out_customer_number === this.details.customer.out_digital_wallet_id);
              }
            });
        }
        if (this.details?.customer) {
          this.prefix = this.details.customer.out_prefix;
        }
        if (!this.prefix && this.prefix.indexOf('.') < 0) {
          this.prefix = `${this.prefix}.onmicrosoft.com`;
        }
      }
    })

    this.activatedRoute.paramMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(param => {
      this.tenantId = param.get('tenantId');

      // Get Azure RIPurchaseRights Status
      this.manageService.getAzureRIPurchaseRights(this.tenantId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((res: string) => {
          this.form.get('azureRIPurchaseToggle').setValue(res === 'granted');
          if (this.currentUserModel?.hasAdminAccess || this.currentUserModel?.hasPurchaseAccess) {
            this.form.get('azureRIPurchaseToggle').enable();
          }
          this.ARIPRTLoadingSpinner = false;
        });

      // Get Azure Cost Management Status
      this.manageService.GetAzureCostManagementPolicy(this.tenantId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((res: string) => {
          this.form.get('azureCMViewToggle').setValue(res === 'Allowed');
          if (this.currentUserModel?.hasAdminAccess || this.currentUserModel?.hasPurchaseAccess) {
            this.form.get('azureCMViewToggle').enable();
          }

          const e = 'Cannot get policy for customer';
          if (res.includes(e)) {
            this.dialog.open(SimpleMsgPopupComponent, {
              width: '400px',
              data: {
                title: 'Azure Permission',
                msg: 'Azure Subscription not found on this tenant. Some management features will be unavailable.'
              }
            }).afterClosed().subscribe(() => {
              this.ACMVTLoadingSpinner = false;
              this.showAzureCMToggle = false;
            });
            
          }

          this.ACMVTLoadingSpinner = false;
        });

      //Azure Savings Plan
      this.manageService.getAzureSavingsPlan(this.tenantId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((res: string) => {
          this.form.get('azureSavingsPlanToggle').setValue(res === 'granted');
          if (this.currentUserModel?.hasAdminAccess || this.currentUserModel?.hasPurchaseAccess) {
            this.form.get('azureSavingsPlanToggle').enable();
          }
          this.AZSPLoadingSpinner = false;
        });
    })
  }

  public editEndUserDetail(event): void {
    event.preventDefault();
    this.router.navigate(['EditEndUser'], { relativeTo: this.activatedRoute });
  }

  public editPaymentDetails(event): void {
    event.preventDefault();
    this.manageService.monitorDetailsChange(this.details);
    this.router.navigate(['EditPaymentDetails'], { relativeTo: this.activatedRoute });
  }

  public toggleAzureRIPurchaseRight(e: MatSlideToggleChange): void {
    this.ARIPRTLoadingSpinner = true;
    this.form.get('azureRIPurchaseToggle').disable()

    this.manageService.ManageAzureRIPermission(this.tenantId, e.checked ? 'grant' : 'revoke')
      .subscribe((res: ApiResponse) => {
        if(res.status === true) {
        this.manageService.getAzureRIPurchaseRights(this.tenantId)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((res: string) => {
            this.form.get('azureRIPurchaseToggle').setValue(res === 'granted');
            this.form.get('azureRIPurchaseToggle').enable();
            this.ARIPRTLoadingSpinner = false;
          })
        }
        else {          
          this.commonService.implementToast(res.responseMessage, "Error");
          this.ARIPRTLoadingSpinner = false;  
          this.form.get('azureRIPurchaseToggle').setValue(false);        
          this.form.get('azureRIPurchaseToggle').enable()
      }
      
      },
      (error: any) => {
        console.info(error);
        this.loadContent = true;
      });
  }
  

  public toggleAzureCMView(e: MatSlideToggleChange): void {
    this.ACMVTLoadingSpinner = true;
    this.form.get('azureCMViewToggle').disable()

    this.manageService.SetAzureCostsPolicy(this.tenantId, e.checked ? 'Allowed' : 'NotAllowed')
      .subscribe((res: string) => {
        this.form.get('azureCMViewToggle').setValue(res === 'Allowed');
        this.form.get('azureCMViewToggle').enable();
        this.ACMVTLoadingSpinner = false;
      });
  }

  public toggleAzureSavingsPlan(e: MatSlideToggleChange): void {
    this.AZSPLoadingSpinner=true;
    this.form.get('azureSavingsPlanToggle').disable()
 
    this.manageService.ManageAzureSavingsPlan(this.tenantId, e.checked ? 'grant' : 'revoke')
       .subscribe((res: string) => {
         this.manageService.getAzureSavingsPlan(this.tenantId)
           .pipe(takeUntil(this.ngUnsubscribe))
           .subscribe((res: string) => {
             this.form.get('azureSavingsPlanToggle').setValue(res === 'granted');
             this.form.get('azureSavingsPlanToggle').enable();
             this.AZSPLoadingSpinner = false;
           })
       });
  }

  public btnClick(url) {
    const win = window.open(url, '_blank');
    win.focus();
  }

  updateAgreement(): void {
    const dialogRef = this.dialog.open(MicrosoftCloudAgreementComponent, {
      width: '630px',
      height: '470px',
      data: {
        firstName: this.subscriptionResponse.agreedVersion.out_first_name,
        lastName: this.subscriptionResponse.agreedVersion.out_last_name,
        email: this.subscriptionResponse.agreedVersion.out_email,
        phone: this.subscriptionResponse.agreedVersion.out_phone,
        status: this.subscriptionResponse.status,
        tenantId: this.tenantId,
        hdnAgreementVersion: this.subscriptionResponse.agreedVersion.out_version,
        link: this.subscriptionResponse.agreementLink
      },
      panelClass: 'mat-dialog-wrapper',
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.manageService.GetMSCloudAgreementStatus(this.tenantId).subscribe((res: SubscriptionApiResponse) => {
          this.isSubscriptionResponseLoading = false;
          this.subscriptionResponse = res;
        },
          (error: any) => {
            console.error(error);
          });
      }
    });
  }

  private isInCustiomerMenu(): boolean {
    return this.router.url.includes('customer');
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

}
